import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { InstallPWAButton } from "@/components/InstallPWAButton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  useAgentRealtime,
  ensureNotificationPermission,
  registerServiceWorker,
  primeAudio,
} from "@/hooks/useAgentRealtime";
import {
  Home, Users, CalendarClock, BellRing, LogOut, Sparkles, Bot, Wifi, WifiOff, BellPlus, Wallet,
} from "lucide-react";
import ReadyPopup from "@/components/ReadyPopup";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { AIManagerWidget } from "@/components/AIManagerWidget";

const NAV = [
  { to: "/app", end: true, icon: Home, label: "Home" },
  { to: "/app/leads", icon: Users, label: "Leads" },
  { to: "/app/followups", icon: CalendarClock, label: "Tasks" },
  { to: "/app/orders", icon: Wallet, label: "Orders" },
  { to: "/app/notifications", icon: BellRing, label: "Alerts" },
];

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  // 1) Register the service worker once — this powers the PWA install prompt
  //    AND lets us route notification-clicks back into the app.
  useEffect(() => { registerServiceWorker(); }, []);

  // 2) When the SW posts a "notification-click" back to us, navigate.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      const msg = event.data;
      if (msg && msg.type === "notification-click" && msg.url) {
        nav(msg.url, { replace: false });
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [nav]);

  // 3) Poll unread count as a fallback (WS pushes are the fast path).
  const loadUnread = useCallback(() => {
    api.get("/notifications", { params: { unread: true } })
      .then((r) => setUnread(r.data.length))
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 30000);
    const onChanged = () => loadUnread();
    window.addEventListener("crm:notifs-changed", onChanged);
    return () => {
      clearInterval(iv);
      window.removeEventListener("crm:notifs-changed", onChanged);
    };
  }, [loadUnread]);

  // 4) Realtime WebSocket — on every notification event refresh the unread badge
  //    + surface an in-app toast so the agent notices even without OS notifs.
  const onEvent = useCallback((msg) => {
    if (msg.type === "notification" && msg.notification) {
      loadUnread();
      const n = msg.notification;
      toast(n.title || "New alert", { description: n.body || "" });
    }
  }, [loadUnread]);
  const { connected } = useAgentRealtime({ enabled: !!user?.id, onEvent });

  // 5) Ask for notification permission (deferred until the user has actually
  //    interacted with the page — enforced by the browser). We attach a
  //    one-shot listener on the first tap.
  useEffect(() => {
    if (permission === "granted" || permission === "denied" || permission === "unsupported") return;
    const askOnce = async () => {
      await primeAudio();
      const p = await ensureNotificationPermission();
      setPermission(p);
      window.removeEventListener("pointerdown", askOnce);
    };
    window.addEventListener("pointerdown", askOnce, { once: true });
    return () => window.removeEventListener("pointerdown", askOnce);
  }, [permission]);

  // Handle server-initiated force logout (admin revoked / single-device kick).
  useEffect(() => {
    const onForce = () => {
      toast.error("Signed out (another device took over).");
      logout().then(() => nav("/login", { replace: true }));
    };
    window.addEventListener("crm:force-logout", onForce);
    return () => window.removeEventListener("crm:force-logout", onForce);
  }, [logout, nav]);

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const enableNotifs = async () => {
    await primeAudio();
    const p = await ensureNotificationPermission();
    setPermission(p);
    if (p === "granted") toast.success("Notifications enabled");
    else if (p === "denied") toast.error("Notifications blocked in browser settings");
  };

  return (
    <div className="grain flex min-h-screen flex-col bg-background pb-20">
      {/* Topbar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-sm font-bold leading-none">Northline</div>
              <div className="mono flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                Team app
                <span
                  data-testid="agent-realtime-status"
                  className={`inline-flex items-center gap-0.5 ${connected ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
                  title={connected ? "Realtime connected" : "Reconnecting…"}
                >
                  {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                </span>
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {permission !== "granted" && permission !== "unsupported" && (
              <button
                onClick={enableNotifs}
                data-testid="enable-notifs-btn"
                title="Enable notifications"
                className="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 text-xs font-semibold text-primary hover:bg-primary/20"
              >
                <BellPlus className="h-3.5 w-3.5" /> Enable alerts
              </button>
            )}
            <ThemeToggle />
            <NotificationBell role="agent" />
            <Button variant="outline" size="sm" onClick={onLogout} data-testid="agent-logout-btn" className="rounded-full">
              <LogOut className="mr-1 h-4 w-4" /> Exit
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 p-4">
        <InstallPWAButton />
        {permission === "default" && (
          <div
            data-testid="notif-permission-banner"
            className="glass mb-4 flex items-center gap-3 rounded-lg p-3 animate-fade-up"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-600">
              <BellPlus className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-bold leading-tight">Turn on lead alerts</div>
              <div className="text-xs text-muted-foreground">Get an instant sound + push when a new lead is assigned.</div>
            </div>
            <button
              onClick={enableNotifs}
              data-testid="enable-notifs-banner-btn"
              className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground tap-lift"
            >
              Allow
            </button>
          </div>
        )}
        <Outlet />
        <ReadyPopup />
      </main>

      <AIManagerWidget />

      {/* Bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto grid max-w-3xl grid-cols-5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`agent-nav-${item.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 py-3 text-xs font-semibold tap-lift ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {item.label === "Alerts" && unread > 0 && (
                <span
                  data-testid="agent-alerts-badge"
                  className="absolute right-4 top-2 h-4 min-w-[16px] rounded-full bg-destructive px-1 text-[10px] font-bold leading-4 text-white"
                >
                  {unread}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
